import React, { Fragment, useEffect, useRef, useState, useContext } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { gql, useQuery, useMutation } from '@apollo/client'
import { GuideContext } from '../../context/guide-context'
import { useOAuth2 } from '../../utils/oauth2-hook'
import { CAMPAIGN_FIELDS } from '../../graphql/campaign-fragments'
import { Row } from 'react-bootstrap'
import Card from '../../components/common/Card'
import PlatformCard from '../../components/campaigns/PlatformCard'
import Notification from '../../components/partials/Notification'
import { CubeLoader } from '@/components/ui/cube-loader'
import { showNotification, askConfirmation } from '../../utils/notifications'
import { logos } from '../../utils/platform-logos'
import _ from 'lodash'
import { GridPlusIcon } from '@/components/icons/new'

const GET_CAMPAIGN = gql`
    ${CAMPAIGN_FIELDS}
    query Campaigns($where: CampaignWhere) {
        campaigns(where: $where) {
            ...CampaignFields
            platforms {
                id
            }
            platformsAggregate {
                count
            }
            landingPagesAggregate {
                count
            }
        }
    }
`

const GET_PLATFORMS = gql`
    query Platforms($options: PlatformOptions) {
        platforms(options: $options) {
            id
            name
            authorizeUrl
            clientId
            scope
            extra
            status
            credentials {
                accessToken
            }
        }
    }
`

const CONNECT_CAMPAIGN_TO_PLATFORM = gql`
    mutation ConnectCampaignToPlatformWithCredentials($oAuthPlatformInput: OAuthPlatformCredentialsInput) {
        connectCampaignToPlatformWithCredentials(oAuthPlatformInput: $oAuthPlatformInput) {
            id
            platforms {
                id
            }
        }
    }
`

const CONNECT_CAMPAIGN_TO_PLATFORM_WITHOUT_AUTH = gql`
    ${CAMPAIGN_FIELDS}
    mutation ConnectCampaignToPlatform($oAuthPlatformInput: OAuthPlatformInput) {
        connectCampaignToPlatform(oAuthPlatformInput: $oAuthPlatformInput) {
            ...CampaignFields
            platforms {
                id
            }
        }
    }
`

const DISCONNECT_CAMPAIGN_FROM_PLATFORM = gql`
    mutation DisconnectCampaignFromPlatform($revokeOAuthPlatformInput: RevokeOAuthPlatformInput) {
        disconnectCampaignFromPlatform(revokeOAuthPlatformInput: $revokeOAuthPlatformInput) {
            id
            platforms {
                id
            }
        }
    }
`

const Platforms = () => {
    const params = useParams()
    const { guide } = useContext(GuideContext)
    const campaignId = params.id
    const navigate = useNavigate()

    const guidedRef = useRef(false)
    const guidedRefCon = useRef(false)
    const pendingPlatformNameRef = useRef(null)

    const [run, setRun] = useState(true)
    const [connected, setConnected] = useState(null)
    const [hasCreatives, setHasCreatives] = useState(null)
    const [steps] = useState([
        {
            target: '.platform-step-0',
            content:
                'If you want your campaign to be integrated with this platform, click "Connect Now" button and follow the prompts on the screen',
        },
    ])

    const campaigns = useQuery(GET_CAMPAIGN, {
        variables: { where: { id: campaignId } },
        onCompleted: (data) => {
            let result = false
            let hasCreatives = false
            data?.campaigns.forEach((campaign) => {
                if (campaign.platformsAggregate.count > 2) {
                    result = true
                }
                if (campaign.landingPagesAggregate.count > 0) {
                    hasCreatives = true
                }
            })
            setConnected(result)
            setHasCreatives(hasCreatives)
        },
    })

    const platforms = useQuery(GET_PLATFORMS, {
        variables: { options: { sort: [{ order: 'ASC' }] } },
    })

    const connectCampaignMutationHandlers = {
        onError({ graphQLErrors }) {
            showNotification(graphQLErrors[0]?.message, 'error')
        },
        onCompleted: () => {
            platforms.refetch()
            const name = pendingPlatformNameRef.current
            showNotification(
                `${name ? `${name} provider` : 'The provider'} has been connected successfully!`,
                'success',
            )
            pendingPlatformNameRef.current = null
        },
    }

    const [connectCampaignToPlatform] = useMutation(CONNECT_CAMPAIGN_TO_PLATFORM, connectCampaignMutationHandlers)
    const [connectCampaignToPlatformWithoutAuth] = useMutation(
        CONNECT_CAMPAIGN_TO_PLATFORM_WITHOUT_AUTH,
        connectCampaignMutationHandlers,
    )

    const clickConnect = async (platformId) => {
        const selectedPlatform = _.filter(platforms.data?.platforms, (platform) => {
            return platform.id === platformId
        })?.[0]

        if (!selectedPlatform) {
            showNotification('Platform not found', 'error')
            return
        }

        pendingPlatformNameRef.current = selectedPlatform.name

        const platformRequiresAuth = !!selectedPlatform.authorizeUrl

        const platformCredentials = selectedPlatform.credentials

        if (platformRequiresAuth && !platformCredentials) return getAuth(selectedPlatform)

        if (platformCredentials)
            return await connectCampaignToPlatform({
                variables: {
                    oAuthPlatformInput: {
                        id: selectedPlatform.id,
                        campaignId: campaignId,
                    },
                },
                onCompleted: (data) => {
                    let result = false
                    if (data?.connectCampaignToPlatformWithCredentials.platforms.length > 2) {
                        result = true
                    }
                    guidedRefCon.current = null
                    setConnected(result)
                },
            })
        return await connectCampaignToPlatformWithoutAuth({
            variables: {
                oAuthPlatformInput: {
                    id: selectedPlatform.id,
                    campaignId: campaignId,
                },
            },
            onCompleted: (data) => {
                let result = false
                if (data?.connectCampaignToPlatform.platforms.length > 2) {
                    result = true
                }
                guidedRefCon.current = null
                setConnected(result)
            },
        })
    }

    const revokePlatform = (args) => {
        if (args?.platformId) {
            disconnectCampaignFromPlatform({
                variables: {
                    revokeOAuthPlatformInput: {
                        id: args.platformId,
                        campaignId: campaignId,
                    },
                },
            })
        }
    }

    const clickDisconnect = (platformId, platformName) => {
        pendingPlatformNameRef.current = platformName
        askConfirmation(
            `You are going to disconnect ${platformName} from this Campaign`,
            'Disconnect',
            revokePlatform,
            { platformId: platformId },
        )
    }

    const { getAuth } = useOAuth2({
        campaignId: campaignId,
        redirectUri: `${document.location.origin}/callback`,
        responseType: 'code',
        extraQueryParameters: {},
    })

    const [disconnectCampaignFromPlatform] = useMutation(DISCONNECT_CAMPAIGN_FROM_PLATFORM, {
        onError({ graphQLErrors }) {
            showNotification(graphQLErrors[0]?.message, 'error')
        },
        onCompleted: () => {
            platforms.refetch()
            const name = pendingPlatformNameRef.current
            showNotification(
                `${name ? `${name} provider` : 'The provider'} has been disconnected successfully!`,
                'success',
            )
            pendingPlatformNameRef.current = null
        },
    })

    const nextStep = () => {
        navigate('/campaigns/' + campaignId + '/landing-pages/')
    }

    useEffect(() => {
        if (connected !== null && guide === 'on') {
            if (!connected && !guidedRef.current) {
                showNotification(
                    <div>
                        <p>
                            Please authenticate the platforms you wish to integrate with using your unique username and
                            password.
                        </p>
                        <p>
                            We recommend authenticating with at least three platforms, preferably Instagram, Twitter,
                            and YouTube.
                        </p>
                        <p>Follow the provided instructions to complete the integration.</p>
                    </div>,
                    'info',
                    false,
                    'Continue',
                    null,
                    false,
                )
                guidedRef.current = true
            }
        }
    }, [connected])

    useEffect(() => {
        if (hasCreatives !== null && guide === 'on') {
            if (!hasCreatives && connected && !guidedRefCon.current) {
                showNotification(
                    <div>
                        <p>Great job! You are successfully connected to the platforms.</p>
                        <p>
                            To connect additional platforms, click the 'X' button in the top-right corner of this
                            message.
                        </p>
                        <p>To proceed, click the "Continue" button.</p>
                    </div>,
                    'info',
                    false,
                    'Continue',
                    () => navigate('/campaigns/' + campaignId + '/landing-pages'),
                    true,
                )
                guidedRefCon.current = true
            }
        }
    }, [hasCreatives, guidedRefCon.current, connected, guide])

    useEffect(() => {
        const checkElements = setInterval(() => {
            const allTargetsMounted = steps.every((step) => document.querySelector(step.target))
            if (allTargetsMounted) {
                clearInterval(checkElements)
                setRun(true)
            }
        }, 100)
    }, [steps])

    if (campaigns.loading || platforms.loading) return <CubeLoader size="default" fullScreen={true} />

    return (
        <Fragment>
            <Card className="page-card">
                <Card.Header>
                    <h5 className="card-title d-flex align-items-center gap-1">
                        <GridPlusIcon className="text-gray-200" />
                        License Platforms Settings - {campaigns.data?.campaigns?.[0]?.name || 'Loading...'}
                    </h5>
                </Card.Header>
                <Card.Body className="pb-0">
                    <Row className="row-cols-xl-4 row-cols-md-2 row-cols-1">
                        {platforms.data?.platforms
                            ?.filter((platform) => platform.status === 'ACTIVE')
                            .map((platform, index) => {
                                return (
                                    <PlatformCard
                                        key={platform.id}
                                        className={'platform-step-' + index}
                                        img={logos[platform.name]}
                                        header={platform.name}
                                        connected={
                                            _.find(campaigns.data?.campaigns?.[0]?.platforms, {
                                                id: platform.id,
                                            }) !== undefined
                                        }
                                        onConnect={() => clickConnect(platform.id)}
                                        onRevoke={() => clickDisconnect(platform.id, platform.name)}
                                    />
                                )
                            })}
                    </Row>
                </Card.Body>
            </Card>
            <Notification />
        </Fragment>
    )
}

export default Platforms
